import React, { useState } from 'react';
import { formatTemp } from '../utils/temperature';

const OUTCOME_COLORS = { verified: '#7ecfc0', partial: '#e8c878', missed: '#e87878', pending: '#4a5a7a' };
const STATE_COLORS = { calm: '#7ecfc0', watch: '#e8c878', alert: '#e8a598', severe: '#e87878' };

export default function LeftPanel({ state, priorCalls, regions, sourceHealth, selectedRegionId, onSelectRegion, tempUnit }) {
  const [expandedRegion, setExpandedRegion] = useState(null);
  const display = state?.display || { color: '#7ecfc0', pulse: false };
  const level = state?.level || 'CALM';
  const confidence = state?.confidence || 0;
  const activeFocus = state?.active_focus || [];
  const confPct = Math.round(confidence * 100);
  const stateColor = STATE_COLORS[level.toLowerCase()] || '#7ecfc0';

  const primaryRegion = (regions || []).find(r => r.type === 'primary');
  const sentinelRegions = (regions || []).filter(r => r.type === 'global_sentinel');
  const onlineCount = (regions || []).filter(r => r.online).length;
  const totalCount = (regions || []).length;

  const topFocus = activeFocus[0];
  const stateDriver = topFocus
    ? `${topFocus.category} activity (${topFocus.detail}) dominating signal field`
    : 'All domains nominal';

  const handleRegionClick = (id) => onSelectRegion(id);
  const toggleExpand = (id) => setExpandedRegion(prev => prev === id ? null : id);

  const renderWeather = (w) => {
    if (!w || w.temperature_c == null) return null;
    return <span className="region-weather">{formatTemp(w.temperature_c, tempUnit)} {w.weather_desc || ''}</span>;
  };

  return (
    <div className="left-panel" data-testid="left-panel">
      {/* Orb */}
      <div className="avatar-wrap">
        <div className="avatar-ring-outer">
          <div className="orbit-ring" style={{ borderColor: `${stateColor}20` }}>
            <div className="orbit-dot" style={{ background: stateColor, boxShadow: `0 0 7px ${stateColor}` }} />
          </div>
          <div className="orbit-ring-inner"><div className="orbit-dot-inner" /></div>
          <div className="avatar-circle" style={{ boxShadow: `0 0 24px ${stateColor}10, 0 0 0 1px ${stateColor}0e` }}>
            <div className="avatar-glow" />
            <svg className="avatar-svg" viewBox="0 0 46 46" fill="none">
              <circle cx="23" cy="23" r="19" stroke={stateColor} strokeWidth="0.5" opacity="0.25" />
              <circle cx="23" cy="23" r="11" stroke={stateColor} strokeWidth="0.4" opacity="0.2" />
              <ellipse cx="18" cy="20" rx="2" ry="2.5" fill={stateColor} opacity="0.9" />
              <ellipse cx="28" cy="20" rx="2" ry="2.5" fill={stateColor} opacity="0.9" />
              <path d="M18 30 Q23 33 28 30" stroke={stateColor} strokeWidth="1.2" fill="none" opacity="0.7" strokeLinecap="round" />
              <circle cx="23" cy="23" r="3.5" fill={stateColor} opacity="0.85" />
            </svg>
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div className="avatar-name">Nova</div>
          <div className="avatar-state" style={{ color: stateColor }} data-testid="avatar-state">{level}</div>
        </div>
        <div className="conf-wrap">
          <div className="conf-label">
            <span className="conf-label-text">CONFIDENCE</span>
            <span className="conf-label-val" style={{ color: stateColor }} data-testid="confidence-value">{confPct}%</span>
          </div>
          <div className="bar-track"><div className="bar-fill" style={{ width: `${confPct}%`, background: `linear-gradient(90deg, ${stateColor}50, ${stateColor})` }} /></div>
        </div>
      </div>

      {/* State Driver */}
      <div className="section-divider" data-testid="state-driver-section">
        <div className="section-label">STATE DRIVER</div>
        <div className="state-driver" data-testid="state-driver">{stateDriver}</div>
        <div className="scope-tag" style={{ color: stateColor, background: `${stateColor}12`, border: `1px solid ${stateColor}25` }}>GLOBAL</div>
      </div>

      {/* Active Focus */}
      <div className="section-divider" data-testid="active-focus-section">
        <div className="section-label">ACTIVE FOCUS</div>
        {activeFocus.map((f, i) => (
          <div className="focus-row" key={i}>
            <span className="focus-key">{f.category}</span>
            <span className="focus-val">{f.detail}</span>
          </div>
        ))}
      </div>

      {/* Source Health */}
      {sourceHealth?.sources && (
        <div className="section-divider" data-testid="source-health-section">
          <div className="section-label">SOURCE HEALTH</div>
          {Object.entries(sourceHealth.sources).map(([name, s]) => (
            <div key={name} style={{ marginBottom: 6 }}>
              <div className="source-row">
                <div className="source-dot" style={{ background: s.healthy ? '#7ecfc0' : s.stale ? '#e8c878' : '#e87878' }} />
                <span className="source-name">{name}</span>
                <span className="source-status">{(s.health_state_v2 || (s.healthy ? 'healthy' : s.stale ? 'delayed' : 'offline')).toUpperCase()}</span>
              </div>
              <div style={{ paddingLeft: 18, fontSize: 10, color: 'rgba(188,205,229,0.68)', lineHeight: 1.4 }}>
                last success: {s.last_success ? new Date(s.last_success).toLocaleTimeString() : '—'} · retries: {s.retry_count ?? 0} · recoveries: {s.recovery_attempts ?? 0}
                {s.degraded_reason ? ` · reason: ${String(s.degraded_reason).slice(0, 80)}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Registered Regions */}
      <div className="section-divider" data-testid="regions-section">
        <div className="section-label">REGIONS ({onlineCount}/{totalCount})</div>
        {primaryRegion && (
          <>
            <div className="region-group-label">PRIMARY</div>
            <div
              className={`region-row region-expandable${selectedRegionId === primaryRegion.id ? ' region-selected' : ''}`}
              data-testid={`region-${primaryRegion.id}`}
            >
              <div className="region-dot" style={{ background: STATE_COLORS[primaryRegion.state] }} />
              <div className="region-info" onClick={() => handleRegionClick(primaryRegion.id)} style={{ cursor: 'pointer' }}>
                <span className="region-name">{primaryRegion.name}</span>
                {renderWeather(primaryRegion.weather)}
              </div>
              <span className="region-signals">{primaryRegion.signal_count}</span>
              <span className="region-expand-icon" onClick={(e) => { e.stopPropagation(); toggleExpand(primaryRegion.id); }} style={{ cursor: 'pointer' }}>
                {expandedRegion === primaryRegion.id ? '\u25B4' : '\u25BE'}
              </span>
            </div>
            {expandedRegion === primaryRegion.id && primaryRegion.subregions?.length > 0 && (
              <div className="subregion-list" data-testid="bahamas-subregions">
                <div className="subregion-header">ANALYSIS ZONES</div>
                {primaryRegion.subregions.map(sr => (
                  <div
                    className={`subregion-row${selectedRegionId === sr.id ? ' region-selected' : ''}`}
                    key={sr.id}
                    onClick={() => handleRegionClick(sr.id)}
                    style={{ cursor: 'pointer' }}
                    data-testid={`subregion-${sr.id}`}
                  >
                    <div className="subregion-dot" style={{ background: STATE_COLORS[sr.state] || '#7ecfc0' }} />
                    <div className="region-info">
                      <span className="subregion-name">{sr.name}</span>
                      {sr.weather && <span className="region-weather">{formatTemp(sr.weather.temperature_c, tempUnit)}</span>}
                    </div>
                    <span className="region-signals">{sr.signal_count}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
        <div className="region-group-label" style={{ marginTop: 8 }}>SENTINEL</div>
        {sentinelRegions.map(r => (
          <div
            className={`region-row${selectedRegionId === r.id ? ' region-selected' : ''}`}
            key={r.id}
            onClick={() => handleRegionClick(r.id)}
            style={{ cursor: 'pointer' }}
            data-testid={`region-${r.id}`}
          >
            <div className="region-dot" style={{ background: STATE_COLORS[r.state] }} />
            <div className="region-info">
              <span className="region-name">{r.name}</span>
              {renderWeather(r.weather)}
            </div>
            <span className="region-signals">{r.signal_count}</span>
          </div>
        ))}
      </div>

      {/* Prior Calls */}
      <div className="section-divider" data-testid="prior-calls-section">
        <div className="section-label">PRIOR CALLS</div>
        {priorCalls?.map((call, i) => (
          <div className="prior-item" key={i}>
            <div className="prior-ev">{call.title}</div>
            <div className="prior-meta">
              <span className="prior-outcome" style={{ color: OUTCOME_COLORS[call.outcome] || '#4a5a7a' }}>{call.outcome}</span>
              <span className="prior-acc">{Math.round(call.accuracy * 100)}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
